/**
 * Module collision / overlap detection.
 *
 * The mover collision (moverCollision.ts) is a 1D arc-length test for two
 * movers on the *same* chain; its header explicitly declines cross-XPU /
 * cross-track cases, which "would need an OBB / mesh test in 3D space".
 * This file is exactly that test, for the physical track *modules*.
 *
 * Each module is modelled as a chain of oriented bounding boxes (OBBs) that
 * follow its real centerline: the path is sampled (sampleModulePath) and each
 * segment becomes a small box (±halfWidth in Y, 0..height in Z) oriented along
 * the local tangent, then transformed into world space by
 *
 *     world = orientation ⊗ trackTransform ⊗ partTransformation ⊗ chainPose
 *
 * Sampling the path (rather than a single straight box per module) is what
 * keeps curves and U-turns faithful — a single straight box would poke
 * outside the arc and produce both false positives and false negatives on a
 * closed oval. Straights collapse to one box; curves / free paths use several.
 *
 * Pairs are tested with the Separating Axis Theorem (15 axes) over their
 * segment boxes, yielding a penetration depth (minimum translation distance)
 * as `overlapMm`:
 *
 *     overlapMm  > 0  — boxes overlap by this many mm (deepest sub-box pair).
 *     overlapMm  = 0  — touching exactly.
 *     overlapMm  < 0  — closest-approach gap (only reported when
 *                       `warningGapMm` is set).
 *
 * Adjacent modules in the same part butt up edge-to-edge (ChainBuilder), so
 * consecutive `(i, i+1)` pairs — and the `(N-1, 0)` seam of a closed loop —
 * are excluded; everything else (cross-part, cross-XPU, same-part
 * non-adjacent) is tested.
 *
 * The box cross-section (`halfWidthMm` 50, `heightMm` 100) is a coarse but
 * deterministic, asset-independent envelope. Real per-module CAD bounds live
 * in the GLB sidecars (`approximateBoundsMm`) and could refine it later.
 * Likewise the `warningGapMm` near-miss is an axis-projected SAT separation,
 * not a true Euclidean (GJK) distance — slightly conservative for
 * corner-to-corner cases.
 */

import { Matrix4, Vector3 } from 'three';
import type { BuiltModule } from './ChainBuilder.js';
import { poseToMatrix4 } from './PathBuilder.js';
import { sampleModulePath } from './pathMath.js';
import { composePartWorldMatrix } from './trackTransform.js';
import { isChainClosed } from './chainClosure.js';
import type { BuiltChain } from './ChainBuilder.js';
import type {
  ModuleRef,
  ModuleType3D,
  Orientation,
  PartTransformation,
  TrackTransform,
  Vec3,
} from '../types.js';

/** Default box half-width (Y) — matches `approximateModuleBounds`. */
export const DEFAULT_MODULE_HALF_WIDTH_MM = 50;
/** Default box height (Z) — matches `approximateModuleBounds`. */
export const DEFAULT_MODULE_HEIGHT_MM = 100;

const DEFAULT_WARNING_GAP_MM = 0;
const EPS = 1e-9;
/** Target arc length per sampled segment box on a curved/free module (mm). */
const SEGMENT_LENGTH_MM = 50;
const DEG2RAD = Math.PI / 180;

/**
 * One detected pair. Symmetric: `a`/`b` are canonicalised so the lower
 * `(processingUnitObjectId, partObjectId, moduleIndex)` tuple is always `a`.
 */
export interface ModuleCollision {
  a: ModuleRef;
  b: ModuleRef;
  moduleTypeA: ModuleType3D;
  moduleTypeB: ModuleType3D;
  /** Forwarded `ModuleEntry.globalNumber` (status-store key), if present. */
  globalNumberA?: number;
  globalNumberB?: number;
  /**
   * Penetration depth along the minimum-translation axis of the deepest
   * sub-box pair:
   *   • `> 0` — overlap (mm).
   *   • `= 0` — touching.
   *   • `< 0` — closest-approach gap (only when `warningGapMm` is set).
   */
  overlapMm: number;
  /** Centre-to-centre distance of the deepest sub-box pair (mm). */
  centerDistanceMm: number;
  /** Unit world-space axis of least penetration (the MTV direction). */
  axis: Vec3;
}

export interface CheckModuleCollisionsOptions {
  /**
   * When `> 0`, near-misses with an axis-projected gap up to this many mm
   * are also returned (with `overlapMm < 0`). Default 0 — only actual
   * overlaps / contacts.
   */
  warningGapMm?: number;
  /**
   * Also test non-adjacent module pairs within the SAME part. Default true.
   * Consecutive `(i, i+1)` pairs and the closed-loop `(N-1, 0)` seam are
   * ALWAYS excluded regardless of this flag.
   */
  includeSamePart?: boolean;
  /** Box half-width in Y (mm). Default 50. */
  halfWidthMm?: number;
  /** Box height in Z (mm). Default 100. */
  heightMm?: number;
}

/** Oriented bounding box in world space (unit axes + scaled half-extents). */
interface Obb {
  center: Vector3;
  axes: [Vector3, Vector3, Vector3];
  halfExtents: [number, number, number];
  /** Bounding-sphere radius (broad-phase reject). */
  radius: number;
}

/** Pre-built per-module probe consumed by `checkModuleCollisions`. */
export interface ModuleProbe {
  ref: ModuleRef;
  moduleType: ModuleType3D;
  globalNumber?: number;
  /** Adjacency grouping key (part.globalNumber — system-wide unique). */
  partKey: number;
  /** Index of this module within its part's chain. */
  moduleIndex: number;
  /** Segment boxes following the module centerline (≥ 1). */
  obbs: Obb[];
  /** Module-level bounding sphere enclosing all segment boxes. */
  sphereCenter: Vector3;
  sphereRadius: number;
}

/** A part fed to `buildModuleProbes`. */
export interface ModuleCollisionPartInput {
  /** ModuleRef.partObjectId (`PartConfig.objectId`). */
  partObjectId: number;
  /** Adjacency key (`PartConfig.globalNumber`). */
  partKey: number;
  partTransformation?: PartTransformation | null;
  chain: BuiltChain;
}

/** An XPU fed to `buildModuleProbes`. */
export interface ModuleCollisionXpuInput {
  xpuObjectId: number;
  /** Effective (override-resolved) per-XPU track transform. */
  trackTransform?: TrackTransform | null;
  parts: ReadonlyArray<ModuleCollisionPartInput>;
}

/**
 * Build a world OBB for a box centred at the given world matrix's origin,
 * with the supplied local half-extents. Returns `null` if any world axis is
 * degenerate (zero scale).
 */
function obbFromCenteredMatrix(
  world: Matrix4,
  hx: number,
  hy: number,
  hz: number,
): Obb | null {
  const e = world.elements; // column-major
  const col0 = new Vector3(e[0], e[1], e[2]);
  const col1 = new Vector3(e[4], e[5], e[6]);
  const col2 = new Vector3(e[8], e[9], e[10]);
  const sx = col0.length();
  const sy = col1.length();
  const sz = col2.length();
  if (sx < EPS || sy < EPS || sz < EPS) return null;

  const axisX = col0.multiplyScalar(1 / sx);
  const axisY = col1.multiplyScalar(1 / sy);
  const axisZ = col2.multiplyScalar(1 / sz);
  const center = new Vector3(e[12], e[13], e[14]);
  const halfExtents: [number, number, number] = [hx * sx, hy * sy, hz * sz];
  const radius = Math.hypot(halfExtents[0], halfExtents[1], halfExtents[2]);
  return { center, axes: [axisX, axisY, axisZ], halfExtents, radius };
}

/**
 * Sample a built module's centerline into world-space segment OBBs. A
 * Straight uses one box; a Curve / Free path uses several so the boxes hug
 * the real arc instead of poking outside it. Returns `[]` for a degenerate
 * (zero-length) module — mirroring the mover predicate's `length <= 0` guard.
 */
function buildModuleObbs(
  builtModule: BuiltModule,
  worldBase: Matrix4,
  halfWidthMm: number,
  heightMm: number,
): Obb[] {
  const cat = builtModule.catalog;
  const length = cat.moduleLengthMm;
  if (length <= EPS) return [];

  const startMat = worldBase
    .clone()
    .multiply(poseToMatrix4(builtModule.startWorldPose));

  const segs =
    cat.pathType === 'Straight'
      ? 1
      : Math.max(1, Math.ceil(length / SEGMENT_LENGTH_MM));

  const halfHeight = heightMm / 2;
  const out: Obb[] = [];
  for (let k = 0; k < segs; k++) {
    const s0 = (length * k) / segs;
    const s1 = (length * (k + 1)) / segs;
    const a = sampleModulePath(cat, s0).positionMm;
    const b = sampleModulePath(cat, s1).positionMm;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const chord = Math.hypot(dx, dy);
    if (chord < EPS) continue;
    const yawDeg = Math.atan2(dy, dx) / DEG2RAD;
    // Segment-local pose: centred between the two samples, lifted to the box
    // mid-height, oriented along the chord (about +Z).
    const segLocal = poseToMatrix4({
      positionMm: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, halfHeight],
      yawDeg,
    });
    const world = startMat.clone().multiply(segLocal);
    // Box spans exactly the chord between the two samples; consecutive
    // segments share a sample point, so they meet without a seam gap and a
    // straight module collapses to its exact [0, length] box.
    const halfLen = chord / 2;
    const obb = obbFromCenteredMatrix(world, halfLen, halfWidthMm, halfHeight);
    if (obb) out.push(obb);
  }
  return out;
}

/**
 * Turn a list of XPUs (with effective track transforms) + parts (with built
 * chains) into flat module probes plus a per-part adjacency map. Shared by
 * the imperative ref and the reactive monitor so the world-matrix
 * composition lives in one place.
 *
 * Hidden XPUs / parts (`visible === false`) are skipped to match the scene.
 */
export function buildModuleProbes(
  orientation: Orientation | undefined,
  xpus: ReadonlyArray<ModuleCollisionXpuInput>,
  opts: CheckModuleCollisionsOptions = {},
): {
  probes: ModuleProbe[];
  adjacency: Map<number, { count: number; closed: boolean }>;
} {
  const halfWidthMm = opts.halfWidthMm ?? DEFAULT_MODULE_HALF_WIDTH_MM;
  const heightMm = opts.heightMm ?? DEFAULT_MODULE_HEIGHT_MM;

  const probes: ModuleProbe[] = [];
  const adjacency = new Map<number, { count: number; closed: boolean }>();

  for (const xpu of xpus) {
    if (xpu.trackTransform?.visible === false) continue;
    for (const part of xpu.parts) {
      if (part.partTransformation?.visible === false) continue;
      const worldBase = composePartWorldMatrix(
        orientation,
        xpu.trackTransform,
        part.partTransformation,
      );
      adjacency.set(part.partKey, {
        count: part.chain.modules.length,
        closed: isChainClosed(part.chain),
      });
      for (let i = 0; i < part.chain.modules.length; i++) {
        const m = part.chain.modules[i]!;
        const obbs = buildModuleObbs(m, worldBase, halfWidthMm, heightMm);
        if (obbs.length === 0) continue;
        const { center, radius } = boundingSphere(obbs);
        const probe: ModuleProbe = {
          ref: {
            processingUnitObjectId: xpu.xpuObjectId,
            partObjectId: part.partObjectId,
            moduleIndex: i,
          },
          moduleType: m.moduleType,
          partKey: part.partKey,
          moduleIndex: i,
          obbs,
          sphereCenter: center,
          sphereRadius: radius,
        };
        if (m.globalNumber !== undefined) probe.globalNumber = m.globalNumber;
        probes.push(probe);
      }
    }
  }

  return { probes, adjacency };
}

/** Bounding sphere enclosing a set of segment OBBs. */
function boundingSphere(obbs: ReadonlyArray<Obb>): {
  center: Vector3;
  radius: number;
} {
  const center = new Vector3();
  for (const o of obbs) center.add(o.center);
  center.multiplyScalar(1 / obbs.length);
  let radius = 0;
  for (const o of obbs) {
    radius = Math.max(radius, center.distanceTo(o.center) + o.radius);
  }
  return { center, radius };
}

/** Projected radius of an OBB onto a unit axis L. */
function projectRadius(obb: Obb, L: Vector3): number {
  return (
    obb.halfExtents[0] * Math.abs(obb.axes[0].dot(L)) +
    obb.halfExtents[1] * Math.abs(obb.axes[1].dot(L)) +
    obb.halfExtents[2] * Math.abs(obb.axes[2].dot(L))
  );
}

/**
 * OBB-vs-OBB SAT with penetration depth. `t = centerB − centerA`.
 * Returns the minimum-translation overlap and its axis, or `null` when the
 * boxes are separated by more than `gapThreshold` on some axis.
 */
function obbOverlap(
  a: Obb,
  b: Obb,
  t: Vector3,
  gapThreshold: number,
): { overlapMm: number; axis: Vector3 } | null {
  const candidates: Vector3[] = [
    a.axes[0],
    a.axes[1],
    a.axes[2],
    b.axes[0],
    b.axes[1],
    b.axes[2],
  ];
  for (const ai of a.axes) {
    for (const bj of b.axes) {
      const c = new Vector3().crossVectors(ai, bj);
      if (c.lengthSq() > EPS) candidates.push(c.normalize());
    }
  }

  let maxSeparation = -Infinity;
  let mtvAxis = candidates[0]!;
  for (const L of candidates) {
    const dist = Math.abs(t.dot(L));
    const separation = dist - (projectRadius(a, L) + projectRadius(b, L));
    if (separation > gapThreshold) return null; // separated beyond the band
    if (separation > maxSeparation) {
      maxSeparation = separation;
      mtvAxis = L;
    }
  }
  return { overlapMm: -maxSeparation, axis: mtvAxis };
}

function refLess(p: ModuleRef, q: ModuleRef): boolean {
  if (p.processingUnitObjectId !== q.processingUnitObjectId) {
    return p.processingUnitObjectId < q.processingUnitObjectId;
  }
  if (p.partObjectId !== q.partObjectId) {
    return p.partObjectId < q.partObjectId;
  }
  return p.moduleIndex < q.moduleIndex;
}

/**
 * Pairwise OBB overlap test over all module probes. Same-part consecutive
 * and closed-loop-seam pairs are excluded; everything else is tested.
 * Returns one entry per overlapping (or near-miss, when `warningGapMm > 0`)
 * pair, sorted deepest-overlap-first.
 */
export function checkModuleCollisions(
  probes: ReadonlyArray<ModuleProbe>,
  adjacency: ReadonlyMap<number, { count: number; closed: boolean }>,
  opts: CheckModuleCollisionsOptions = {},
): ModuleCollision[] {
  const warningGapMm = opts.warningGapMm ?? DEFAULT_WARNING_GAP_MM;
  const includeSamePart = opts.includeSamePart ?? true;
  const gap = Math.max(0, warningGapMm);

  const out: ModuleCollision[] = [];

  for (let i = 0; i < probes.length; i++) {
    const a = probes[i]!;
    for (let j = i + 1; j < probes.length; j++) {
      const b = probes[j]!;

      if (a.partKey === b.partKey) {
        if (!includeSamePart) continue;
        const di = Math.abs(a.moduleIndex - b.moduleIndex);
        if (di === 1) continue; // consecutive seam (butt edge-to-edge)
        const info = adjacency.get(a.partKey);
        if (info?.closed) {
          const lo = Math.min(a.moduleIndex, b.moduleIndex);
          const hi = Math.max(a.moduleIndex, b.moduleIndex);
          if (lo === 0 && hi === info.count - 1) continue; // wrap seam
        }
      }

      // Module-level broad phase: bounding-sphere reject.
      if (
        a.sphereCenter.distanceTo(b.sphereCenter) >
        a.sphereRadius + b.sphereRadius + gap
      ) {
        continue;
      }

      // Narrow phase: deepest overlap over all segment-box pairs.
      let bestOverlap = -Infinity;
      let bestAxis: Vector3 | null = null;
      let bestCenterDist = 0;
      for (const sa of a.obbs) {
        for (const sb of b.obbs) {
          const tVec = new Vector3().subVectors(sb.center, sa.center);
          const d = tVec.length();
          if (d > sa.radius + sb.radius + gap) continue; // sub-box reject
          const hit = obbOverlap(sa, sb, tVec, gap);
          if (!hit) continue;
          if (hit.overlapMm > bestOverlap) {
            bestOverlap = hit.overlapMm;
            bestAxis = hit.axis;
            bestCenterDist = d;
          }
        }
      }
      if (!bestAxis || bestOverlap < -gap) continue;

      const aFirst = refLess(a.ref, b.ref);
      const lo = aFirst ? a : b;
      const hi = aFirst ? b : a;
      const collision: ModuleCollision = {
        a: lo.ref,
        b: hi.ref,
        moduleTypeA: lo.moduleType,
        moduleTypeB: hi.moduleType,
        overlapMm: bestOverlap,
        centerDistanceMm: bestCenterDist,
        axis: [bestAxis.x, bestAxis.y, bestAxis.z],
      };
      if (lo.globalNumber !== undefined) {
        collision.globalNumberA = lo.globalNumber;
      }
      if (hi.globalNumber !== undefined) {
        collision.globalNumberB = hi.globalNumber;
      }
      out.push(collision);
    }
  }

  out.sort((x, y) => y.overlapMm - x.overlapMm);
  return out;
}
